import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, filter, map, mergeMap, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { of } from 'rxjs';

import * as AuthSelectors from 'src/app/auth/store/auth.selectors';
import * as ChannelsSelectors from './channels.selectors';
import * as AuthActions from 'src/app/auth/store/auth.actions';
import * as ChannelsActions from './channels.actions';
import { ChannelsService } from 'src/app/shared/services/channels.service';
import { Channel } from 'src/app/shared/api/types/GraphQL';
import { GqlErrorService } from 'src/app/shared/services/gql-error.service';

@Injectable()
export class ChannelsEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private channelsService: ChannelsService,
    private gqlErrorService: GqlErrorService
  ) {}

  /**
   * Trigger channels load whenever a tenant is established (login/app-load) or changed.
   */
  public readonly loadChannelsOnTenantChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.setTenants, AuthActions.setActiveTenant),
      withLatestFrom(
        this.store.select(AuthSelectors.selectActiveTenant),
        this.store.select(ChannelsSelectors.selectChannelsLoadedTenantId),
        this.store.select(ChannelsSelectors.selectChannelsLoading)
      ),
      filter(([, activeTenant, loadedTenantId, loading]) => {
        const tenantId = activeTenant?.id ? Number(activeTenant.id) : null;
        return !!tenantId && !loading && tenantId !== loadedTenantId;
      }),
      map(() => ChannelsActions.loadChannels())
    )
  );

  public readonly loadChannels$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChannelsActions.loadChannels),
      withLatestFrom(this.store.select(AuthSelectors.selectActiveTenant)),
      switchMap(([, activeTenant]) =>
        this.channelsService.getChannels().valueChanges.pipe(
          // The data/error filter must come BEFORE take(1) (NBO-2158 rule): a data-less
          // loading emission would consume the take and drop the real result. Inert
          // under today's cache-first policy, load-bearing if the query ever goes
          // cache-and-network.
          filter((result) => !!result.data || !!result.error),
          take(1),
          map((result) => {
            // Apollo 4 delivers GraphQL errors as a next result — without this the
            // failure action would carry a TypeError from reading empty data.
            if (result.error) throw result.error;
            return ChannelsActions.loadChannelsSuccess({
              tenantId: Number(activeTenant!.id),
              channels: result.data.channels as Channel[]
            });
          }),
          catchError((error) => of(ChannelsActions.loadChannelsFailure({ error })))
        )
      )
    )
  );

  public readonly loadChannelsFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ChannelsActions.loadChannelsFailure),
        tap(({ error }) => this.gqlErrorService.getGqlError(error))
      ),
    { dispatch: false }
  );

  public readonly channelCreate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChannelsActions.channelCreate),
      mergeMap((action) =>
        this.channelsService.channelCreate(action.input).pipe(
          mergeMap((result) => [
            ChannelsActions.channelCreateSuccess({ channel: result.data!.channelCreate }),
            ChannelsActions.loadChannels()
          ]),
          catchError((error) => of(ChannelsActions.channelCreateFailure({ error })))
        )
      )
    )
  );

  public readonly channelUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChannelsActions.channelUpdate),
      mergeMap((action) =>
        this.channelsService.channelUpdate(action.id, action.input).pipe(
          mergeMap((result) => [
            ChannelsActions.channelUpdateSuccess({ channel: result.data!.channelUpdate }),
            ChannelsActions.loadChannels()
          ]),
          catchError((error) => of(ChannelsActions.channelUpdateFailure({ error })))
        )
      )
    )
  );

  /**
   * Bulk-safe: uses mergeMap so parallel deletes don't cancel each other, and does not
   * re-dispatch loadChannels — the caller batches the success/failure tally and dispatches
   * loadChannels once after the whole batch settles.
   */
  public readonly channelDelete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChannelsActions.channelDelete),
      mergeMap((action) =>
        this.channelsService.channelDelete(action.id).pipe(
          map(() => ChannelsActions.channelDeleteSuccess({ id: action.id })),
          catchError((error) => of(ChannelsActions.channelDeleteFailure({ id: action.id, error })))
        )
      )
    )
  );
}
